/**
 * Le fil de la séance du tuteur, côté données : la séance (`liveSession.users` et leurs conversations avec le
 * champion) mise en formes de présentation.
 *
 * Ports littéraux :
 *  - les résumés du champion de `common/liveMode/AgentSummaryModal.js` : la liste (sauf un démarrage encore en
 *    cours, `starting_block` / `starting_block_study_program`), la plus récente d'abord, « NOUVEAU » sur les n
 *    premières (n = le compteur du champion à l'ouverture), le titre, la puce, l'avis demandé seulement sur une
 *    conversation finie dont le dernier message n'attend pas de réponse (`showFeedbackButtons && isLastMessage`),
 *    les photos (`attachments | checkpoint_attachments | student_response_attachments | media`) ;
 *  - le statut d'une activité rendue, lu dans les listes de l'élève (`activities`, `todo_activities`) : la carte
 *    du rendu vit tant qu'il attend validation (tech-derisk, point 12).
 */
import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { type UICorrectionFeedback } from "../../../ui/types/studentActivity"
import { type UITutorQuizOutcome, type UITutorSamSummary } from "../../../ui/types/tutorLive"
import { PREREQUISITE_QUIZ_CONTEXT } from "../../../../../engine/live/room/checkpointContext"

export type ApiAgentConversation = {
    id?: string | number
    student_id?: string | number
    live_session_id?: string | number
    activity_id?: string | number | null
    status?: string | null
    trigger_context?: string | null
    summary_title?: string | null
    summary?: string | null
    tutor_needed?: boolean | null
    created_at?: string | null
    ended_at?: string | null
    tutor_feedback?: number | null
    agent_message?: { proposed_responses?: unknown[] | null } | null
    attachments?: string[] | null
    checkpoint_attachments?: string[] | null
    student_response_attachments?: string[] | null
    media?: string[] | null
}

export type ApiLiveSessionUser = {
    id?: string | number
    is_tutor?: boolean | null
    firstname?: string | null
    display_name?: string | null
    conversations?: ApiAgentConversation[] | null
}

export type ApiStudentActivity = {
    id?: string | number
    status?: string | null
    school_subject_id?: string | number | null
    finished_at?: string | null
    material?: {
        id?: string | number | null
        title?: string | null
        school_subject_id?: string | number | null
        level?: string | number | null
        class?: string | null
        structured_data?: { analysis?: { confidence?: string | null } | null } | null
    } | null
    student_chapter?: { chapter?: { id?: string | number | null } | null } | null
}
export type ApiStudentLists = {
    activities?: ApiStudentActivity[] | null
    todo_activities?: ApiStudentActivity[] | null
}

const STARTING_BLOCKS = new Set(["starting_block", "starting_block_study_program"])
/**
 * Une conversation qui est en fait le quiz de prérequis de l'élève (`trigger_context`) : le tuteur ne la voit jamais
 * comme le champion — ni ligne du champion, ni résumé, ni carte ; une ligne de quiz (`quizLines`).
 */
export function isQuizConversation(conversation: { trigger_context?: string | null } | null | undefined): boolean {
    return conversation?.trigger_context === PREREQUISITE_QUIZ_CONTEXT
}
const FEEDBACK_STATUSES = new Set(["ended", "tutor_needed", "new_activity_generation"])

/** Un démarrage encore en cours ne compte pas (`AgentSummaryModal.agentSummaries`, le réducteur du compteur). */
export function isCountedConversation(conversation: ApiAgentConversation): boolean {
    return !(conversation.status === "ongoing" && STARTING_BLOCKS.has(conversation.trigger_context ?? ""))
}

function time(conversation: ApiAgentConversation): number {
    const at = Date.parse(conversation.created_at ?? "")
    return Number.isNaN(at) ? 0 : at
}

/** « 17:20 », l'heure locale sur deux chiffres. */
export function clockOf(ms: number): string {
    const date = new Date(ms)
    const pad = (value: number) => String(value).padStart(2, "0")
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function feedbackOf(conversation: ApiAgentConversation, given?: number): UICorrectionFeedback | null | undefined {
    const value = given ?? conversation.tutor_feedback
    if (value === 1) return "positive"
    if (value === -1) return "negative"
    const asked =
        FEEDBACK_STATUSES.has(conversation.status ?? "") &&
        (conversation.agent_message?.proposed_responses == null ||
            conversation.agent_message.proposed_responses.length === 0)
    return asked ? null : undefined
}

/** Les photos d'un échange, adresses nettoyées (`renderAttachments`). */
export function conversationPhotos(conversation: ApiAgentConversation): string[] {
    const list =
        conversation.attachments ??
        conversation.checkpoint_attachments ??
        conversation.student_response_attachments ??
        conversation.media ??
        []
    return list.map((url) => cleanMediaUrl(url)).filter((url): url is string => url != null)
}

/**
 * Les résumés du champion avec un élève (`AgentSummaryModal`) : `newCount` est le compteur lu à l'ouverture,
 * `given` les avis donnés pendant cette ouverture (`feedbacks`).
 */
export function toUITutorSamSummaries(
    conversations: ApiAgentConversation[] | null | undefined,
    options: { newCount?: number; given?: Record<string, number> } = {}
): UITutorSamSummary[] {
    const newCount = options.newCount ?? 0
    return (conversations ?? [])
        .filter(isCountedConversation)
        .filter((conversation) => !isQuizConversation(conversation))
        .filter((conversation) => conversation.id != null)
        .sort((a, b) => time(b) - time(a))
        .map((conversation, index): UITutorSamSummary => {
            const ongoing = conversation.status === "ongoing"
            return {
                id: String(conversation.id),
                title: ongoing ? "Discussion non terminée" : conversation.summary_title || "Discussion terminée",
                time: clockOf(time(conversation)),
                status: ongoing ? "unfinished" : conversation.status === "interrupted" ? "interrupted" : "finished",
                isNew: index < newCount || undefined,
                needsHelp: conversation.status === "tutor_needed" || undefined,
                generatedActivity: conversation.status === "new_activity_generation" || undefined,
                summary: conversation.summary ?? "",
                photos: conversationPhotos(conversation),
                feedback: feedbackOf(conversation, options.given?.[String(conversation.id)]),
            }
        })
}

/** Les élèves inscrits de la séance (`liveSession.users` sans les tuteurs), en chaînes. */
export function enrolledStudents(users: ApiLiveSessionUser[] | null | undefined): ApiLiveSessionUser[] {
    return (users ?? []).filter((user) => user?.id != null && user.is_tutor !== true)
}

/**
 * Les résumés finis de cette séance, une ligne du champion chacun dans l'historique du fil (`helper`). Ils se
 * relisent du serveur (`origin: server`) : ils suivent sur un autre appareil.
 */
export type TutorHelperLine = { id: string; studentId: string; at: number; title?: string; text: string }

export function helperLines(users: ApiLiveSessionUser[] | null | undefined, liveSessionId: string): TutorHelperLine[] {
    const lines: TutorHelperLine[] = []
    for (const user of enrolledStudents(users)) {
        for (const conversation of user.conversations ?? []) {
            if (String(conversation.live_session_id) !== liveSessionId) continue
            if (conversation.status === "ongoing" || !conversation.summary?.trim() || conversation.id == null) continue
            if (isQuizConversation(conversation)) continue
            const ended = Date.parse(conversation.ended_at ?? conversation.created_at ?? "")
            lines.push({
                id: `helper-${conversation.id}`,
                studentId: String(user.id),
                at: Number.isNaN(ended) ? 0 : ended,
                title: conversation.summary_title ?? undefined,
                text: conversation.summary,
            })
        }
    }
    return lines
}

/**
 * Le chapitre d'une activité de l'élève (`student_chapter.chapter`) et sa matière : la photo du chat que le tuteur
 * transforme en activité reprend le chapitre de celle que l'élève a devant lui. Aucun chapitre : `null`.
 */
export function activityChapter(
    student: ApiStudentLists | null | undefined,
    activityId: string | undefined
): { chapterId: string; subjectId: string | null } | null {
    if (student == null || activityId == null) return null
    const all = [...(student.activities ?? []), ...(student.todo_activities ?? [])]
    const activity = all.find((candidate) => String(candidate.id) === activityId)
    const chapterId = activity?.student_chapter?.chapter?.id
    if (chapterId == null) return null
    const subjectId = activity?.school_subject_id ?? activity?.material?.school_subject_id
    return { chapterId: String(chapterId), subjectId: subjectId != null ? String(subjectId) : null }
}

/** Le statut d'une activité de l'élève, là où ses listes la portent ; `null` si on ne la connaît pas. */
export function activityReviewStatus(
    student: ApiStudentLists | null | undefined,
    activityId: string | undefined
): string | null {
    if (student == null || activityId == null) return null
    const all = [...(student.activities ?? []), ...(student.todo_activities ?? [])]
    return all.find((activity) => String(activity.id) === activityId)?.status ?? null
}

/** D'où vient la similaire pré-remplie : l'activité ouverte au centre, la dernière rendue, celle en cours. */
export type TutorGenerationPrefillKind = "open" | "submitted" | "current"

/** L'activité d'où part une « similaire » pré-remplie : de quoi la poser en support dans le champ. */
export type TutorGenerationPrefillSource = {
    activityId: string
    materialId: string | number
    title: string | null
    level: number | null
    confidence: string | null
    subjectId: string | number | null
    className: string | null
    /** Ce que la relecture montre de l'original (l'activité ouverte au centre le porte). */
    preview?: { statement?: string; imageUri?: string }
}

/**
 * Ce que le champ reprend quand le tuteur ouvre la génération, dans l'ordre de Wajih (04/11) :
 *  0. `open` : l'activité que le tuteur a ouverte ou montre au centre — c'est d'elle qu'il parle (live du 04/11 au
 *     soir : ouverte sur une activité, il recevait une similaire de la dernière rendue) ;
 *  1. `submitted` : la dernière activité rendue dans la séance — c'est sur elle que l'élève travaille ;
 *  2. `current` : l'activité qu'il a devant lui, sinon la première de sa liste à faire ;
 *  3. `history` : le chapitre de sa dernière activité faite, sinon la matière de l'objectif de la séance.
 * `null` : rien à reprendre, le champ part de la classe et de la matière de l'élève.
 */
export type TutorGenerationPrefill =
    | { kind: TutorGenerationPrefillKind; source: TutorGenerationPrefillSource }
    | { kind: "history"; subjectId: string | null; chapterId: string | null }

function prefillSource(activity: ApiStudentActivity): TutorGenerationPrefillSource | null {
    const material = activity.material
    if (activity.id == null || material?.id == null) return null
    const level = parseInt(String(material.level ?? ""), 10)
    return {
        activityId: String(activity.id),
        materialId: material.id,
        title: material.title ?? null,
        level: Number.isNaN(level) ? null : level,
        confidence: material.structured_data?.analysis?.confidence ?? null,
        subjectId: material.school_subject_id ?? activity.school_subject_id ?? null,
        className: material.class ?? null,
    }
}

/** « 2026-11-04 12:10 », l'heure locale du serveur : l'espace devient un « T », que Hermes sait lire. */
const finishedAt = (activity: ApiStudentActivity) => {
    const at = Date.parse((activity.finished_at ?? "").replace(" ", "T"))
    return Number.isNaN(at) ? null : at
}

export function generationPrefill(input: {
    student: ApiStudentLists | null | undefined
    /** L'instant où la séance a commencé (ms) : « rendue dans la séance » se lit depuis lui. */
    sessionStartedAt: number
    /** L'activité devant l'élève (`onScreen`), s'il en a une. */
    onScreenId?: string | null
    /** Les matières de l'objectif de la séance, dans l'ordre du serveur (`sessionSubjectIds`). */
    objectiveSubjectIds?: (string | number)[]
    /** L'activité ouverte au centre pour cet élève, s'il y en a une à support. */
    open?: TutorGenerationPrefillSource | null
}): TutorGenerationPrefill | null {
    if (input.open != null) return { kind: "open", source: input.open }
    const done = input.student?.activities ?? []
    const todo = input.student?.todo_activities ?? []
    const byRecent = [...done, ...todo]
        .filter((activity) => finishedAt(activity) != null)
        .sort((a, b) => (finishedAt(b) ?? 0) - (finishedAt(a) ?? 0))

    const submitted = byRecent.find((activity) => (finishedAt(activity) ?? 0) >= input.sessionStartedAt)
    const submittedSource = submitted != null ? prefillSource(submitted) : null
    if (submittedSource != null) return { kind: "submitted", source: submittedSource }

    const onScreen =
        input.onScreenId != null ? todo.find((activity) => String(activity.id) === input.onScreenId) : undefined
    const current = [onScreen, ...todo].map((activity) => (activity != null ? prefillSource(activity) : null))
    const currentSource = current.find((source) => source != null)
    if (currentSource != null) return { kind: "current", source: currentSource }

    for (const activity of byRecent) {
        const chapterId = activity.student_chapter?.chapter?.id
        if (chapterId == null) continue
        const subjectId = activity.school_subject_id ?? activity.material?.school_subject_id
        return {
            kind: "history",
            subjectId: subjectId != null ? String(subjectId) : null,
            chapterId: String(chapterId),
        }
    }
    const objectiveSubject = input.objectiveSubjectIds?.[0]
    if (objectiveSubject != null) return { kind: "history", subjectId: String(objectiveSubject), chapterId: null }
    return null
}

/** Le score d'un quiz, quand son résumé le porte (« … avec un score de 4/5 », `QuizService::submitAttempt`). */
export function quizScoreOf(summary: string | null | undefined): string | undefined {
    const score = /score de (\d+)\s*\/\s*(\d+)/i.exec(summary ?? "")
    return score != null ? `${score[1]}/${score[2]}` : undefined
}

/** Comment un quiz s'est fini : réussi (fermé `ended`), interrompu, sinon pas réussi (`tutor_needed`). */

/** Un quiz de prérequis fini dans cette séance, et son score. */
export type TutorQuizLine = { id: string; studentId: string; at: number; outcome: UITutorQuizOutcome; score?: string }

export function quizOutcomeOf(status: string | null | undefined): UITutorQuizOutcome {
    if (status === "ended") return "passed"
    if (status === "interrupted") return "interrupted"
    return "failed"
}

/**
 * Les quiz de prérequis finis de cette séance, une ligne de quiz chacun dans l'historique du fil. Ils se relisent du
 * serveur, comme les lignes du champion : ils suivent sur un autre appareil.
 */
export function quizLines(users: ApiLiveSessionUser[] | null | undefined, liveSessionId: string): TutorQuizLine[] {
    const lines: TutorQuizLine[] = []
    for (const user of enrolledStudents(users)) {
        for (const conversation of user.conversations ?? []) {
            if (String(conversation.live_session_id) !== liveSessionId || !isQuizConversation(conversation)) continue
            if (conversation.status === "ongoing" || conversation.id == null) continue
            const ended = Date.parse(conversation.ended_at ?? conversation.created_at ?? "")
            lines.push({
                id: `quiz-${conversation.id}`,
                studentId: String(user.id),
                at: Number.isNaN(ended) ? 0 : ended,
                outcome: quizOutcomeOf(conversation.status),
                score: quizScoreOf(conversation.summary),
            })
        }
    }
    return lines
}
